<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import {
  acknowledgeLibraryDocument,
  addLibraryDocument,
  getDownloadUrl,
  getLibraryAcknowledgements,
  listLibraryDocuments,
  removeLibraryDocument,
  setLibraryDocAckRequired,
  uploadFile,
  type LibraryAckStatus,
  type LibraryDocument,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["library-documents", selectedProjectId],
  queryFn: () => listLibraryDocuments(selectedProjectId.value),
});

// Documents the current user still needs to read + acknowledge.
const needsMyAck = computed(() =>
  (data.value ?? []).filter(
    (d) => d.acknowledgement_required && !d.acknowledged_by_me,
  ),
);
const filter = ref<"all" | "needs">("all");
const visibleDocs = computed(() =>
  filter.value === "needs" ? needsMyAck.value : (data.value ?? []),
);
const projectDocs = computed(() =>
  visibleDocs.value.filter((d) => d.scope === "project"),
);
const orgDocs = computed(() => visibleDocs.value.filter((d) => d.scope === "org"));

async function acknowledge(id: string): Promise<void> {
  await acknowledgeLibraryDocument(id);
  await queryClient.invalidateQueries({ queryKey: ["library-documents"] });
}
async function toggleAckRequired(d: LibraryDocument): Promise<void> {
  await setLibraryDocAckRequired(d.id, !d.acknowledgement_required);
  await queryClient.invalidateQueries({ queryKey: ["library-documents"] });
}

// "See who hasn't signed" modal.
const whoDoc = ref<LibraryDocument | null>(null);
const whoStatus = ref<LibraryAckStatus | null>(null);
const whoBusy = ref(false);
async function openWho(d: LibraryDocument): Promise<void> {
  whoDoc.value = d;
  whoStatus.value = null;
  whoBusy.value = true;
  try {
    whoStatus.value = await getLibraryAcknowledgements(d.id);
  } finally {
    whoBusy.value = false;
  }
}

const KINDS = [
  { value: "specification", label: "Specification" },
  { value: "drawing", label: "Drawing" },
  { value: "standard", label: "Standard" },
  { value: "policy", label: "Policy" },
  { value: "procedure", label: "Procedure" },
  { value: "certificate", label: "Certificate" },
  { value: "other", label: "Other" },
];
const kindLabel = (k: string): string =>
  KINDS.find((x) => x.value === k)?.label ?? k;

const showAdd = ref(false);
const form = ref({
  scope: "project" as "project" | "org",
  visibility: "all" as "all" | "office",
  kind: "specification",
  title: "",
  url: "",
  file: null as File | null,
  acknowledgementRequired: false,
});
const fileInput = ref<HTMLInputElement | null>(null);
const busy = ref(false);
const err = ref<string | null>(null);
const canAdd = computed(() => form.value.title.trim().length > 0);

function onFile(e: Event): void {
  form.value.file = (e.target as HTMLInputElement).files?.[0] ?? null;
}

async function add(): Promise<void> {
  if (!canAdd.value) return;
  busy.value = true;
  err.value = null;
  try {
    const fileId = form.value.file
      ? await uploadFile(form.value.file, "library_document")
      : undefined;
    await addLibraryDocument({
      scope: form.value.scope,
      visibility: form.value.visibility,
      kind: form.value.kind,
      title: form.value.title.trim(),
      projectId: selectedProjectId.value as string,
      fileId,
      url: form.value.url.trim() || undefined,
      acknowledgementRequired: form.value.acknowledgementRequired,
    });
    form.value.title = "";
    form.value.url = "";
    form.value.file = null;
    form.value.acknowledgementRequired = false;
    if (fileInput.value) fileInput.value.value = "";
    showAdd.value = false;
    await queryClient.invalidateQueries({ queryKey: ["library-documents"] });
  } catch (e) {
    err.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

async function open(fileId: string | null, url: string | null): Promise<void> {
  err.value = null;
  try {
    if (url) {
      window.open(url, "_blank");
    } else if (fileId) {
      window.open(await getDownloadUrl(fileId), "_blank");
    }
  } catch (e) {
    err.value = e instanceof Error ? e.message : String(e);
  }
}

function remove(id: string): void {
  if (!window.confirm("Remove this document from the library?")) return;
  void (async () => {
    busy.value = true;
    try {
      await removeLibraryDocument(id);
      await queryClient.invalidateQueries({ queryKey: ["library-documents"] });
    } finally {
      busy.value = false;
    }
  })();
}
</script>

<template>
  <PageLayout>
    <div class="mb-1 flex items-start justify-between gap-4">
      <h1 class="text-xl font-semibold">
        Documents
      </h1>
      <button
        type="button"
        class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        @click="showAdd = !showAdd"
      >
        {{ showAdd ? "Cancel" : "+ Add document" }}
      </button>
    </div>
    <p class="mb-4 text-sm text-neutral-500">
      Reference documentation for this project and company-wide. "Office only"
      documents are hidden from operatives.
    </p>

    <!-- Acknowledgement prompt + filter -->
    <div
      v-if="needsMyAck.length"
      class="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-semantic-warning/40 bg-semantic-warning/5 px-4 py-2.5 text-sm"
    >
      <span class="font-medium text-neutral-900">
        {{ needsMyAck.length }} file(s) below need your acknowledgement.
      </span>
      <button
        type="button"
        class="rounded-md border border-semantic-warning/50 px-2.5 py-1 text-xs font-medium hover:bg-semantic-warning/10"
        @click="filter = filter === 'needs' ? 'all' : 'needs'"
      >
        {{ filter === "needs" ? "Show all files" : "Show only these" }}
      </button>
    </div>
    <div class="mb-4 flex gap-2 text-sm">
      <button
        type="button"
        class="rounded-full px-3 py-1"
        :class="filter === 'all' ? 'bg-brand-primary text-brand-primary-on' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'"
        @click="filter = 'all'"
      >
        All files
      </button>
      <button
        type="button"
        class="rounded-full px-3 py-1"
        :class="filter === 'needs' ? 'bg-brand-primary text-brand-primary-on' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'"
        @click="filter = 'needs'"
      >
        Needs my acknowledgement ({{ needsMyAck.length }})
      </button>
    </div>

    <!-- Add form -->
    <div
      v-if="showAdd"
      class="mb-4 rounded-xl border border-neutral-200 bg-neutral-0 p-4"
    >
      <div class="grid gap-3 sm:grid-cols-2">
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Library</span>
          <select
            v-model="form.scope"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
            <option value="project">
              This project
            </option>
            <option value="org">
              Company-wide (all projects)
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Visibility</span>
          <select
            v-model="form.visibility"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
            <option value="all">
              Everyone
            </option>
            <option value="office">
              Office / managers only
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Type</span>
          <select
            v-model="form.kind"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
            <option
              v-for="k in KINDS"
              :key="k.value"
              :value="k.value"
            >
              {{ k.label }}
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Title</span>
          <input
            v-model="form.title"
            type="text"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Link (URL) — or upload a file below</span>
          <input
            v-model="form.url"
            type="text"
            placeholder="https://…"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">File</span>
          <input
            ref="fileInput"
            type="file"
            class="text-sm"
            @change="onFile"
          >
        </label>
        <label class="flex items-start gap-2 text-sm sm:col-span-2">
          <input
            v-model="form.acknowledgementRequired"
            type="checkbox"
            class="mt-0.5"
          >
          <span>
            <span class="font-medium text-neutral-900">Requires acknowledgement</span>
            <span class="block text-xs text-neutral-500">
              Everyone with app access must read it and confirm — for master
              RAMS, policies, standards.
            </span>
          </span>
        </label>
      </div>
      <div class="mt-3 flex items-center gap-3">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="!canAdd || busy"
          @click="add"
        >
          {{ busy ? "Adding…" : "Add document" }}
        </button>
        <span
          v-if="err"
          class="text-sm text-semantic-danger"
        >{{ err }}</span>
      </div>
    </div>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else
      class="grid gap-6"
    >
      <section
        v-for="grp in [
          { title: 'Project documents', docs: projectDocs },
          { title: 'Company-wide documents', docs: orgDocs },
        ]"
        :key="grp.title"
        class="rounded-xl bg-neutral-0 shadow overflow-hidden"
      >
        <h2 class="px-6 pb-3 pt-6 font-semibold">
          {{ grp.title }} ({{ grp.docs.length }})
        </h2>
        <p
          v-if="grp.docs.length === 0"
          class="px-6 pb-6 text-sm text-neutral-400"
        >
          None yet.
        </p>
        <ul
          v-else
          class="divide-y divide-neutral-100"
        >
          <li
            v-for="d in (grp.docs as LibraryDocument[])"
            :key="d.id"
            class="px-6 py-2.5 text-sm"
          >
            <div class="flex items-center gap-3">
              <span class="rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                {{ kindLabel(d.kind) }}
              </span>
              <button
                type="button"
                class="text-brand-primary hover:underline"
                @click="open(d.file_id, d.url)"
              >
                {{ d.title }}
              </button>
              <span
                v-if="d.visibility === 'office'"
                class="rounded bg-semantic-warning/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-semantic-warning"
              >Office only</span>
              <span
                v-if="d.acknowledgement_required"
                class="rounded bg-brand-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-brand-primary"
              >Acknowledgement required</span>
              <span class="ml-auto text-xs text-neutral-400">
                {{ d.added_by_name ?? "—" }}
              </span>
              <button
                type="button"
                class="text-neutral-300 hover:text-semantic-danger"
                title="Remove"
                :disabled="busy"
                @click="remove(d.id)"
              >
                ✕
              </button>
            </div>

            <!-- Acknowledgement row -->
            <div
              v-if="d.acknowledgement_required"
              class="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-1 text-xs"
            >
              <span
                v-if="d.acknowledged_by_me"
                class="inline-flex items-center gap-1 font-medium text-semantic-success"
              >✓ You acknowledged this</span>
              <button
                v-else
                type="button"
                class="rounded-md bg-brand-primary px-2.5 py-1 font-semibold text-brand-primary-on hover:opacity-90"
                @click="acknowledge(d.id)"
              >
                I've read and understood
              </button>
              <span class="text-neutral-500">
                {{ d.acknowledged_count }}<template v-if="d.required_total != null"> of {{ d.required_total }}</template>
                acknowledged
              </span>
              <button
                type="button"
                class="text-brand-primary hover:underline"
                @click="openWho(d)"
              >
                See who hasn't signed
              </button>
            </div>

            <!-- Management toggle for the acknowledgement requirement -->
            <div class="mt-1 pl-1">
              <button
                type="button"
                class="text-[11px] text-neutral-400 hover:text-neutral-600 hover:underline"
                @click="toggleAckRequired(d)"
              >
                {{ d.acknowledgement_required ? "Stop requiring acknowledgement" : "Require acknowledgement" }}
              </button>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <!-- Who hasn't signed -->
    <div
      v-if="whoDoc"
      class="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      @click.self="whoDoc = null"
    >
      <div class="max-h-[80vh] w-full max-w-md overflow-hidden rounded-xl bg-neutral-0 shadow-xl">
        <div class="flex items-start justify-between gap-4 border-b border-neutral-100 px-5 py-4">
          <div>
            <h3 class="font-semibold">
              Acknowledgement status
            </h3>
            <p class="text-xs text-neutral-500">
              {{ whoDoc.title }}
            </p>
          </div>
          <button
            type="button"
            class="text-neutral-400 hover:text-neutral-700"
            @click="whoDoc = null"
          >
            ✕
          </button>
        </div>
        <div class="max-h-[60vh] overflow-y-auto px-5 py-4 text-sm">
          <p
            v-if="whoBusy"
            class="text-neutral-500"
          >
            Loading…
          </p>
          <template v-else-if="whoStatus">
            <p class="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-400">
              Not yet acknowledged ({{ whoStatus.outstanding.length }})
            </p>
            <ul
              v-if="whoStatus.outstanding.length"
              class="mb-4 divide-y divide-neutral-100"
            >
              <li
                v-for="(p, i) in whoStatus.outstanding"
                :key="'o' + i"
                class="flex items-center justify-between py-1.5"
              >
                <span>{{ p.name ?? p.email ?? "—" }}</span>
                <span class="text-xs text-semantic-warning">outstanding</span>
              </li>
            </ul>
            <p
              v-else
              class="mb-4 text-xs text-semantic-success"
            >
              Everyone has acknowledged.
            </p>

            <p class="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-400">
              Acknowledged ({{ whoStatus.acknowledged.length }})
            </p>
            <ul
              v-if="whoStatus.acknowledged.length"
              class="divide-y divide-neutral-100"
            >
              <li
                v-for="(p, i) in whoStatus.acknowledged"
                :key="'a' + i"
                class="flex items-center justify-between py-1.5"
              >
                <span>{{ p.name ?? p.email ?? "—" }}</span>
                <span class="text-xs text-neutral-400">
                  {{ p.acknowledged_at ? new Date(p.acknowledged_at).toLocaleDateString() : "" }}
                </span>
              </li>
            </ul>
            <p
              v-else
              class="text-xs text-neutral-400"
            >
              No acknowledgements yet.
            </p>
          </template>
        </div>
      </div>
    </div>
  </PageLayout>
</template>
